"use client";

import { Paginator, Text, SkeletonText } from "@zapier/design-system";
import { useEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { PAGE_VIEW_EVENT } from "@zapier/canvas-constants";
import {
  sessionAtom,
  emitEventOnNewCanvasCreationSetter,
  currentAccountIdAtom,
} from "@zapier/canvas-state";
import { AiPromptBox } from "@zapier/streaming";

import { CanvasListLayout } from "../../components/CanvasListLayout";
import { Guides } from "../../components/Guides";

import { CanvasListItem } from "./CanvasListItem";
import { ZeroState } from "./ZeroState";
import { useWootric } from "./useWootric";
import styles from "./CanvasListPage.module.css";
import {
  CANVAS_LIST_LIMIT,
  canvasesByPageResponseLoadable,
  ownersByPageAtom,
  pageIndexAtom,
  visitedPagesAtom,
} from "./canvasesByPageAtomFamily";

function SkeletonRow() {
  const SkeletonCell = () => (
    <SkeletonText
      color="GrayWarm3"
      lines={1}
      size="body"
      textAlign="left"
      width="100%"
    />
  );

  return (
    <li className={styles.listItem}>
      <div className={styles.linkWrapper}>
        <div className={styles.linkRow}>
          <div className={styles.loadingCol}>
            <SkeletonCell />
          </div>
          <div className={styles.loadingCol}>
            <SkeletonCell />
          </div>
          <div className={styles.loadingCol}>
            <SkeletonCell />
          </div>
          <div className={styles.loadingCol}>
            <SkeletonCell />
          </div>
        </div>
      </div>
      <div className={styles.actionMenuBlank}>
        <span className={styles.visuallyHidden}>Loading</span>
      </div>
    </li>
  );
}

export function CanvasListPage() {
  const session = useAtomValue(sessionAtom);
  const currentAccountId = useAtomValue(currentAccountIdAtom);
  const canvasesResponse = useAtomValue(canvasesByPageResponseLoadable);

  const isCanvasListLoading = canvasesResponse.state === "loading";
  const response =
    canvasesResponse.state === "hasData"
      ? canvasesResponse.data
      : {
          results: null,
          meta: null,
        };
  const canvases = response.results;
  const meta = response.meta;
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const membersLoadable = useAtomValue(ownersByPageAtom);
  const isOwnerLoading = membersLoadable.state === "loading";
  const members =
    membersLoadable.state === "hasData" ? membersLoadable.data : [];
  const setVisitedPages = useSetAtom(visitedPagesAtom);

  useWootric({ canvasCount: canvases?.length || 0, session });
  const emitEventOnNewCanvasCreation = useSetAtom(
    emitEventOnNewCanvasCreationSetter,
  );

  useEffect(() => {
    if (window.EventToolkit?.emit && currentAccountId) {
      window.EventToolkit?.emit(
        {
          system_name: "canvas",
          account_id: currentAccountId,
        },
        PAGE_VIEW_EVENT,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastKnownCount = useRef(0);

  useEffect(() => {
    if (meta && meta?.count > 0) {
      lastKnownCount.current = meta.count;
    }
  }, [meta]);

  // Calculate how many skeleton rows should appear on the current page:
  // - If we have no count data yet, default to 5 skeleton rows for initial loading state
  // - Otherwise, show either the page limit or remaining items (if on last page)
  // This ensures we show the correct number of skeleton rows during loading,
  // preventing layout shifts when data arrives
  const numberOfSkeletonRows =
    (meta?.count || lastKnownCount.current) === 0
      ? 5
      : Math.min(
          CANVAS_LIST_LIMIT,
          lastKnownCount.current - pageIndex * CANVAS_LIST_LIMIT,
        );

  const haveCanvases = !isCanvasListLoading && canvases && canvases.length > 0;

  const handleCreateCanvas = async () => {
    await emitEventOnNewCanvasCreation();
    /* Triggers a full page reload (don't use router.push or else we will have
    bugs when you press back button and press "Create" button again - see CNVS-1731) */
    window.location.href = "/app/canvas/new";
  };

  const pageUrls = Array.from(
    { length: Math.ceil((meta?.count || 0) / CANVAS_LIST_LIMIT) },
    (_, i) => `/page${i}`,
  );

  const shouldShowZeroState = !isCanvasListLoading && meta && meta.count === 0;

  return (
    <CanvasListLayout handleCreateCanvas={handleCreateCanvas} title="Canvas">
      {(haveCanvases || isCanvasListLoading) && (
        <div className={styles.root}>
          <AiPromptBox
            handleSubmit={(prompt) =>
              (window.location.href = `/app/canvas/ai?prompt=${encodeURIComponent(prompt)}`)
            }
          />
          <div>
            <Text type="SectionHeader" color="TextDefault">
              Recent work
            </Text>
            <Header />
            {isCanvasListLoading ? (
              <ul className={styles.list}>
                {[...Array(numberOfSkeletonRows)].map((_, index) => (
                  <SkeletonRow key={index} />
                ))}
              </ul>
            ) : (
              <ul className={styles.list}>
                {canvases?.map((canvas) => (
                  <CanvasListItem
                    key={canvas.id}
                    canvas={canvas}
                    owner={
                      (Array.isArray(members) &&
                        members?.find(
                          (member) => member.id === canvas.customuser_id,
                        )) ||
                      undefined
                    }
                    isOwnerLoading={isOwnerLoading}
                  />
                ))}
              </ul>
            )}
            {meta && meta.count > CANVAS_LIST_LIMIT && (
              <div className={styles.paginator}>
                <Paginator
                  currentPageIndex={pageIndex}
                  isCentered={true}
                  onPaginate={(data, event) => {
                    event.preventDefault();
                    setPageIndex(data.pageIndex);
                    setVisitedPages((prev) => {
                      if (!prev.includes(data.pageIndex)) {
                        return [...prev, data.pageIndex];
                      }
                      return prev;
                    });
                  }}
                  pageUrls={pageUrls}
                />
              </div>
            )}
          </div>
          <Guides />
        </div>
      )}
      {shouldShowZeroState && <ZeroState />}
    </CanvasListLayout>
  );
}

function Header() {
  return (
    <div className={styles.tableHeader}>
      <Text type="SmallPrint2" color="TextDefault">
        Name
      </Text>
      <Text type="SmallPrint2" color="TextDefault">
        Steps
      </Text>
      <Text type="SmallPrint2" color="TextDefault">
        Last modified
      </Text>
      <Text type="SmallPrint2" color="TextDefault">
        Owner
      </Text>
    </div>
  );
}
